import React, { useState, useEffect, useMemo } from 'react';
import { 
  Briefcase, MapPin, Clock, DollarSign, Users, Send, Heart, 
  TrendingUp, Award, CheckCircle, Coffee, Code, Globe, Rocket, X,
  Sparkles, ArrowRight, Zap, Target, GraduationCap, Building,
  FileText, Phone, Mail, Calendar, Shield, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    jobId: '',
    experience: '',
    resume: null,
    coverLetter: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
    fetchJobs();
  }, []);

  // Validate form
  const validateForm = () => {
    const errors = {};
    if (!applicationForm.name.trim()) errors.name = 'Name is required';
    if (!applicationForm.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applicationForm.email)) {
      errors.email = 'Please enter a valid email';
    }
    if (!applicationForm.phone.trim()) errors.phone = 'Phone is required';
    if (!applicationForm.jobId) errors.jobId = 'Please select a position';
    if (!applicationForm.experience.trim()) errors.experience = 'Experience is required';
    if (!applicationForm.resume) errors.resume = 'Resume is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Memoized benefits data with enhanced styling
  const benefits = useMemo(() => [
    {
      icon: <Heart className="w-8 h-8 md:w-10 md:h-10" />,
      title: "Health Insurance",
      description: "Comprehensive medical coverage for you and your family",
      gradient: "from-red-500 to-pink-500",
      color: "text-red-600 dark:text-red-400",
      delay: "0s"
    },
    {
      icon: <TrendingUp className="w-8 h-8 md:w-10 md:h-10" />,
      title: "Career Growth",
      description: "Clear career progression path with regular promotions",
      gradient: "from-green-500 to-emerald-500",
      color: "text-green-600 dark:text-green-400",
      delay: "0.1s"
    },
    {
      icon: <Clock className="w-8 h-8 md:w-10 md:h-10" />,
      title: "Flexible Hours",
      description: "Work-life balance with flexible working hours",
      gradient: "from-blue-500 to-cyan-500",
      color: "text-blue-600 dark:text-blue-400",
      delay: "0.2s"
    },
    {
      icon: <Award className="w-8 h-8 md:w-10 md:h-10" />,
      title: "Learning Budget",
      description: "Annual budget for courses and certifications",
      gradient: "from-purple-500 to-indigo-500",
      color: "text-purple-600 dark:text-purple-400",
      delay: "0.3s"
    },
    {
      icon: <Users className="w-8 h-8 md:w-10 md:h-10" />,
      title: "Team Events",
      description: "Regular team outings and celebration events",
      gradient: "from-orange-500 to-yellow-500",
      color: "text-orange-600 dark:text-orange-400",
      delay: "0.4s"
    },
    {
      icon: <DollarSign className="w-8 h-8 md:w-10 md:h-10" />,
      title: "Performance Bonus",
      description: "Annual performance-based incentives",
      gradient: "from-teal-500 to-green-500",
      color: "text-teal-600 dark:text-teal-400",
      delay: "0.5s"
    }
  ], []);

  // Memoized culture data
  const culture = useMemo(() => [
    {
      icon: <Code className="w-8 h-8 md:w-10 md:h-10" />,
      title: "Cutting-Edge Tech",
      description: "Work with latest technologies and frameworks",
      stat: "20+ Tech Stacks",
      gradient: "from-blue-500 to-purple-500",
      delay: "0s"
    },
    {
      icon: <Users className="w-8 h-8 md:w-10 md:h-10" />,
      title: "Collaborative Team",
      description: "Learn from experienced professionals",
      stat: "15+ Team Members",
      gradient: "from-purple-500 to-pink-500",
      delay: "0.1s"
    },
    {
      icon: <Coffee className="w-8 h-8 md:w-10 md:h-10" />,
      title: "Great Environment",
      description: "Modern office with all amenities",
      stat: "5-Star Rated",
      gradient: "from-pink-500 to-rose-500",
      delay: "0.2s"
    },
    {
      icon: <Rocket className="w-8 h-8 md:w-10 md:h-10" />,
      title: "Fast Growth",
      description: "Rapidly growing startup with global presence",
      stat: "50+ Projects",
      gradient: "from-orange-500 to-amber-500",
      delay: "0.3s"
    }
  ], []);

  // Memoized stats
  const stats = useMemo(() => [
    { 
      value: "15+", 
      label: "Team Members", 
      icon: <Users className="w-8 h-8 md:w-10 md:h-10" />,
      gradient: "from-blue-500 to-cyan-500",
      delay: "0s"
    },
    { 
      value: "50+", 
      label: "Projects Delivered", 
      icon: <Briefcase className="w-8 h-8 md:w-10 md:h-10" />,
      gradient: "from-purple-500 to-pink-500",
      delay: "0.1s"
    },
    { 
      value: "250+", 
      label: "Happy Clients", 
      icon: <Heart className="w-8 h-8 md:w-10 md:h-10" />,
      gradient: "from-green-500 to-emerald-500",
      delay: "0.2s"
    },
    { 
      value: "2+", 
      label: "Countries Served", 
      icon: <Globe className="w-8 h-8 md:w-10 md:h-10" />,
      gradient: "from-orange-500 to-yellow-500",
      delay: "0.3s"
    }
  ], []);

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/careers`);
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', applicationForm.name);
      formData.append('email', applicationForm.email);
      formData.append('phone', applicationForm.phone);
      formData.append('jobId', applicationForm.jobId);
      formData.append('experience', applicationForm.experience);
      formData.append('coverLetter', applicationForm.coverLetter);
      formData.append('resume', applicationForm.resume);

      const response = await fetch(`${API_URL}/api/applications`, {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        alert('Application submitted successfully! We will contact you soon.');
        setApplicationForm({
          name: '',
          email: '',
          phone: '',
          jobId: '',
          experience: '',
          resume: null,
          coverLetter: ''
        });
        setFormErrors({});
        document.getElementById('resume-upload').value = '';
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyClick = (job) => {
    setApplicationForm({
      ...applicationForm,
      jobId: job._id
    });
    setSelectedJob(null);
    document.getElementById('apply-form').scrollIntoView({ behavior: 'smooth' });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setApplicationForm(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex p-4 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl mb-4">
            <Briefcase className="w-8 h-8 text-white animate-pulse" />
          </div>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Loading Opportunities
          </div>
          <p className="text-gray-600 dark:text-gray-400">Discovering amazing career paths...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-64 h-64 md:w-96 md:h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 md:w-[32rem] md:h-[32rem] bg-purple-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className={`transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
                <Sparkles className="w-4 h-4 mr-2" />
                <span className="text-sm font-medium">Build Your Future With Us</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight">
                <span className="block bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
                  Join Our Team
                </span>
                <span className="block text-3xl sm:text-4xl md:text-5xl mt-4 text-blue-200">
                  Shape the Future
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed max-w-2xl">
                Build your career with NEVXOAULIO VENTURES. Work on cutting-edge projects with talented professionals in a dynamic environment.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/30">
                  <Briefcase className="w-5 h-5" />
                  <span className="font-semibold">{jobs.length} Open Positions</span>
                </div>
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-xl border border-white/30">
                  <Users className="w-5 h-5" />
                  <span className="font-semibold">15+ Team Members</span>
                </div>
              </div>
            </div>
            
            <div className={`hidden lg:block transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                  alt="Team collaboration"
                  className="rounded-3xl shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center text-white text-4xl shadow-2xl animate-float">
                  🚀
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className={`group bg-white dark:bg-gray-900 p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-800 animate-fade-in-up hover:-translate-y-2`}
                style={{ animationDelay: stat.delay }}
              >
                <div className={`flex justify-center mb-4 w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${stat.gradient} rounded-2xl items-center justify-center text-white transform group-hover:scale-110 transition-transform`}>
                  {stat.icon}
                </div>
                <div className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-2 text-center font-mono">
                  {stat.value}
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-center text-sm sm:text-base md:text-lg font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-4">
              <Building className="w-4 h-4 mr-2 text-white" />
              <span className="text-sm font-medium text-white">Our Work Culture</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
              Our <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Culture</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              What makes NEVXOAULIO a great place to work and grow
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {culture.map((item, index) => (
              <div 
                key={index}
                className={`group bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 dark:border-gray-800 hover:shadow-2xl transition-all duration-500 animate-fade-in-up hover:-translate-y-3`}
                style={{ animationDelay: item.delay }}
              >
                <div className={`w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center text-white mx-auto mb-4 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  {item.icon}
                </div>
                <div className="text-2xl md:text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2 text-center">
                  {item.stat}
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">
                  {item.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 text-center leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mb-4">
              <Award className="w-4 h-4 mr-2 text-white" />
              <span className="text-sm font-medium text-white">Employee Benefits</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
              Why Work With <span className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">Us?</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              We offer more than just a job - we offer a career and a culture you'll love
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`group relative bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-lg border border-gray-100 dark:border-gray-800 hover:shadow-2xl transition-all duration-500 animate-fade-in-up hover:-translate-y-3`}
                style={{ animationDelay: benefit.delay }}
              >
                <div className="relative z-10">
                  <div className={`w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${benefit.gradient} rounded-2xl flex items-center justify-center text-white mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    {benefit.icon}
                  </div>
                  <h3 className={`text-xl md:text-2xl font-bold mb-3 ${benefit.color} group-hover:scale-105 transition-transform`}>
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
                
                <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full mb-4">
              <Target className="w-4 h-4 mr-2 text-white" />
              <span className="text-sm font-medium text-white">Current Opportunities</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
              Open <span className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent">Positions</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Find your perfect role from our current openings and join our innovative team
            </p>
          </div>

          {jobs.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-block bg-white dark:bg-gray-900 rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-200 dark:border-gray-800 max-w-2xl">
                <div className="w-24 h-24 md:w-32 md:h-32 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Briefcase className="w-12 h-12 md:w-16 md:h-16 text-white" />
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                  No Open Positions
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4 text-lg">
                  We don't have any open positions at the moment, but check back soon!
                </p>
                <p className="text-gray-500 dark:text-gray-500 text-sm">
                  Feel free to send us your resume for future opportunities
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6 md:space-y-8">
              {jobs.map((job) => (
                <div
                  key={job._id}
                  className={`group bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-lg border border-gray-200 dark:border-gray-800 hover:shadow-2xl transition-all duration-500 cursor-pointer hover:border-blue-500 dark:hover:border-blue-500 animate-fade-in-up`}
                  onClick={() => setSelectedJob(job)}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 md:gap-8">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-4">
                        <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
                          <Briefcase className="w-8 h-8 md:w-10 md:h-10 text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{job.title}</h3>
                          <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">{job.department}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-400 mb-4">
                        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-full">
                          <MapPin className="w-4 h-4" />
                          <span className="font-medium">{job.location}</span>
                        </div>
                        <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-full">
                          <Clock className="w-4 h-4" />
                          <span className="font-medium">{job.type}</span>
                        </div>
                        {job.salary && (
                          <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-3 py-2 rounded-full">
                            <DollarSign className="w-4 h-4" />
                            <span className="font-medium">{job.salary}</span>
                          </div>
                        )}
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 line-clamp-2 text-base md:text-lg">
                        {job.description}
                      </p>
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApplyClick(job);
                      }}
                      className="group relative px-8 py-3 md:py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 whitespace-nowrap shadow-lg hover:shadow-2xl hover:scale-105 active:scale-95"
                      aria-label={`Apply for ${job.title}`}
                    >
                      <span className="relative z-10">Apply Now</span>
                      <ChevronRight className="inline ml-2 w-4 h-4 relative z-10 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Application Form */}
      <section id="apply-form" className="py-16 md:py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex p-4 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl mb-4 shadow-lg">
              <Send className="w-8 h-8 md:w-10 md:h-10 text-white" />
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
              Apply <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Now</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Fill out the form below to submit your application. We review every application carefully.
            </p>
          </div>

          <form onSubmit={handleApplicationSubmit} className={`bg-white dark:bg-gray-900 rounded-3xl p-6 md:p-8 lg:p-12 shadow-2xl border border-gray-200 dark:border-gray-800 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  value={applicationForm.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3.5 border rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white transition-all ${
                    formErrors.name 
                      ? 'border-red-500 focus:border-red-500' 
                      : 'border-gray-300 dark:border-gray-700 focus:border-blue-500'
                  }`}
                  placeholder="John Doe"
                  aria-label="Full name"
                />
                {formErrors.name && (
                  <p className="text-sm text-red-600 dark:text-red-400">{formErrors.name}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={applicationForm.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3.5 border rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white transition-all ${
                    formErrors.email 
                      ? 'border-red-500 focus:border-red-500' 
                      : 'border-gray-300 dark:border-gray-700 focus:border-blue-500'
                  }`}
                  placeholder="john@example.com"
                  aria-label="Email address"
                />
                {formErrors.email && (
                  <p className="text-sm text-red-600 dark:text-red-400">{formErrors.email}</p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={applicationForm.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-3.5 border rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white transition-all ${
                    formErrors.phone 
                      ? 'border-red-500 focus:border-red-500' 
                      : 'border-gray-300 dark:border-gray-700 focus:border-blue-500'
                  }`}
                  placeholder="+91 98765 43210"
                  aria-label="Phone number"
                />
                {formErrors.phone && (
                  <p className="text-sm text-red-600 dark:text-red-400">{formErrors.phone}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Position Applied For *
                </label>
                <select
                  name="jobId"
                  required
                  value={applicationForm.jobId}
                  onChange={handleChange}
                  className={`w-full px-4 py-3.5 border rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white transition-all ${
                    formErrors.jobId 
                      ? 'border-red-500 focus:border-red-500' 
                      : 'border-gray-300 dark:border-gray-700 focus:border-blue-500'
                  }`}
                  aria-label="Position"
                >
                  <option value="">Select Position</option>
                  {jobs.map(job => (
                    <option key={job._id} value={job._id}>{job.title}</option>
                  ))}
                </select>
                {formErrors.jobId && (
                  <p className="text-sm text-red-600 dark:text-red-400">{formErrors.jobId}</p>
                )}
              </div>
            </div>

            <div className="mb-6 space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Years of Experience *
              </label>
              <input
                type="text"
                name="experience"
                required
                value={applicationForm.experience}
                onChange={handleChange}
                className={`w-full px-4 py-3.5 border rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white transition-all ${
                  formErrors.experience 
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-gray-300 dark:border-gray-700 focus:border-blue-500'
                }`}
                placeholder="e.g., 5 years"
                aria-label="Experience"
              />
              {formErrors.experience && (
                <p className="text-sm text-red-600 dark:text-red-400">{formErrors.experience}</p>
              )}
            </div>

            <div className="mb-6 space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Upload Resume (PDF only) *
              </label>
              <input
                id="resume-upload"
                type="file"
                name="resume"
                accept=".pdf"
                required
                onChange={(e) => {
                  const file = e.target.files[0];
                  handleChange(e);
                  if (!file) return;
                  const maxSizeBytes = 1 * 1024 * 1024;
                  if (file.size > maxSizeBytes) {
                    alert('Resume must be at most 1 MB in size.');
                    e.target.value = '';
                    setApplicationForm({ ...applicationForm, resume: null });
                    setFormErrors(prev => ({ ...prev, resume: 'File must be less than 1MB' }));
                    return;
                  }
                  setApplicationForm({ ...applicationForm, resume: file });
                  setFormErrors(prev => ({ ...prev, resume: '' }));
                }}
                className={`w-full px-4 py-3.5 border rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-700 dark:file:text-blue-400 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/50 transition-all ${
                  formErrors.resume 
                    ? 'border-red-500 focus:border-red-500' 
                    : 'border-gray-300 dark:border-gray-700 focus:border-blue-500'
                }`}
                aria-label="Resume upload"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum file size: 1MB • PDF format only</p>
              {formErrors.resume && (
                <p className="text-sm text-red-600 dark:text-red-400">{formErrors.resume}</p>
              )}
            </div>

            <div className="mb-8 space-y-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Cover Letter (Optional)
              </label>
              <textarea
                name="coverLetter"
                value={applicationForm.coverLetter}
                onChange={handleChange}
                rows="6"
                className="w-full px-4 py-3.5 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white resize-none transition-all focus:border-blue-500"
                placeholder="Tell us why you'd be a great fit for this role and what excites you about joining our team..."
                aria-label="Cover letter"
              ></textarea>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="group relative w-full px-8 py-4 md:py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98]"
              aria-label="Submit application"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
              
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white relative z-10"></div>
                  <span className="relative z-10 text-lg">Submitting...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 md:w-6 md:h-6 relative z-10 group-hover:rotate-12 transition-transform" />
                  <span className="text-lg relative z-10">Submit Application</span>
                </>
              )}
            </button>

            <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-800">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span>Your information is secure and private</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>We review applications within 48 hours</span>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Job Detail Modal */}
      {selectedJob && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedJob(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="job-modal-title"
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 md:p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4 md:gap-6">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
                    <Briefcase className="w-8 h-8 md:w-10 md:h-10 text-white" />
                  </div>
                  <div>
                    <h2 id="job-modal-title" className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{selectedJob.title}</h2>
                    <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">{selectedJob.department}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:scale-110 transition-transform"
                  aria-label="Close modal"
                >
                  <X className="w-6 h-6 md:w-8 md:h-8" />
                </button>
              </div>

              <div className="flex flex-wrap gap-3 mb-6">
                <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded-full">
                  <MapPin className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedJob.location}</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded-full">
                  <Clock className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedJob.type}</span>
                </div>
                {selectedJob.salary && (
                  <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 px-4 py-2 rounded-full">
                    <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-300">{selectedJob.salary}</span>
                  </div>
                )}
              </div>

              <p className="text-gray-700 dark:text-gray-300 mb-6 text-base md:text-lg leading-relaxed">{selectedJob.description}</p>

              {selectedJob.requirements && selectedJob.requirements.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3">Requirements</h3>
                  <ul className="space-y-2">
                    {selectedJob.requirements.map((req, index) => (
                      <li key={index} className="flex items-start space-x-2 text-gray-700 dark:text-gray-300">
                        <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedJob.responsibilities && selectedJob.responsibilities.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3">Responsibilities</h3>
                  <ul className="space-y-2">
                    {selectedJob.responsibilities.map((resp, index) => (
                      <li key={index} className="flex items-start space-x-2 text-gray-700 dark:text-gray-300">
                        <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span>{resp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                onClick={() => handleApplyClick(selectedJob)}
                className="group relative w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2"
                aria-label="Apply for this position"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
                <Send className="w-5 h-5 relative z-10" />
                <span className="relative z-10">Apply for this Position</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          33% {
            transform: translateY(-20px) rotate(5deg);
          }
          66% {
            transform: translateY(10px) rotate(-5deg);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          opacity: 0;
        }
        
        .animate-fade-in {
          animation: fadeIn 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        /* Mobile Optimization */
        @media (max-width: 768px) {
          .text-7xl {
            font-size: 3.5rem;
          }
          
          .text-6xl {
            font-size: 3rem;
          }
          
          .text-5xl {
            font-size: 2.5rem;
          }
          
          .text-4xl {
            font-size: 2rem;
          }
          
          .text-3xl {
            font-size: 1.75rem;
          }
        }

        /* Reduced motion preference */
        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in-up,
          .animate-fade-in,
          .animate-float,
          .transform,
          .transition-all,
          .transition-transform,
          .transition-opacity {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}